import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import useLenis from "../hooks/useLenis";
import useSeo from "../hooks/useSeo";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../lib/reveal";
import { getCaseStudy, caseStudies } from "../data/caseStudies";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function CaseStudyDetail() {
  useLenis();
  const { slug } = useParams();
  const { t } = useLang();
  const study = getCaseStudy(slug);

  useSeo(
    study
      ? {
          title: `${study.city} · Fresh Earth OED Case Study`,
          description: study.summary,
          path: `/case-studies/${slug}`,
          image: study.afterImg,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: study.headline,
            about: `${study.city}, ${study.region}`,
            image: [study.beforeImg, study.afterImg],
            publisher: { "@type": "Organization", name: "Fresh Earth" },
            description: study.summary,
          },
        }
      : { title: "Case study not found · Fresh Earth", path: `/case-studies/${slug}` }
  );

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!study) {
    return (
      <div className="bg-bone text-ink min-h-screen flex flex-col items-center justify-center" data-testid="case-not-found">
        <p className="font-serif text-3xl">Case study not found.</p>
        <Link to="/case-studies" className="mt-4 underline text-moss">Back to all deployments</Link>
      </div>
    );
  }

  const idx = caseStudies.findIndex((c) => c.slug === slug);
  const next = caseStudies[(idx + 1) % caseStudies.length];

  return (
    <div className="bg-bone text-ink min-h-screen" data-testid="case-detail-page">
      <Navbar />
      <main className="pt-32 md:pt-40">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Link to="/case-studies" data-testid="case-back" className="inline-flex items-center gap-2 text-sm tracking-[0.08em] uppercase text-slate hover:text-ink transition-colors duration-300">
            <ArrowLeft size={16} /> {t("All deployments", "सभी तैनातियाँ")}
          </Link>

          <Reveal className="mt-8">
            <div className="flex items-center gap-3 text-xs tracking-[0.14em] uppercase text-stone">
              <span className="text-terracotta font-semibold">{study.tag}</span>
              <span>·</span>
              <span>{study.city}, {study.region}</span>
            </div>
          </Reveal>
          <Reveal>
            <h1 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.98] mt-4 max-w-4xl">{t(study.headline, study.hi?.headline)}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-slate max-w-2xl">{t(study.summary, study.hi?.summary)}</p>
          </Reveal>

          {/* Metrics */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12">
              {study.metrics.map((m) => (
                <div key={m.k} className="bg-sand rounded-2xl border border-stone/20 p-7">
                  <div className="font-serif text-5xl text-moss leading-none">{m.v}</div>
                  <div className="text-xs tracking-[0.12em] uppercase text-ink mt-3">{m.k}</div>
                  <div className="text-xs text-stone mt-1">{m.note}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Before / After */}
          <Reveal delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-16">
              <div>
                <div className="text-xs tracking-[0.16em] uppercase text-terracotta font-semibold mb-3">{t("Before", "पहले")}</div>
                <div className="rounded-3xl overflow-hidden aspect-[4/3] border border-stone/20">
                  <img src={study.beforeImg} alt="Site before OED treatment" className="w-full h-full object-cover" />
                </div>
              </div>
              <div>
                <div className="text-xs tracking-[0.16em] uppercase text-moss font-semibold mb-3">{t("After", "बाद में")}</div>
                <div className="rounded-3xl overflow-hidden aspect-[4/3] border border-stone/20">
                  <img src={study.afterImg} alt="Site after OED treatment" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* Narrative */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-10">
            {[["Challenge", "चुनौती", study.challenge], ["Approach", "दृष्टिकोण", study.approach], ["Outcome", "परिणाम", study.outcome]].map(([label, labelHi, body]) => (
              <Reveal key={label}>
                <h3 className="font-serif text-2xl mb-3 text-ink">{t(label, labelHi)}</h3>
                <p className="text-base leading-relaxed text-slate">{body}</p>
              </Reveal>
            ))}
          </div>

          {/* Quote */}
          <Reveal>
            <div className="my-20 bg-forest text-bone rounded-3xl p-10 md:p-14 grain relative overflow-hidden">
              <Quote size={40} className="text-terracotta relative" />
              <p className="font-serif text-3xl md:text-4xl leading-snug mt-6 relative max-w-3xl">“{study.quote}”</p>
              <p className="text-sm tracking-[0.12em] uppercase text-bone/60 mt-6 relative">— {study.quoteBy}</p>
            </div>
          </Reveal>
        </div>

        {/* Next + CTA */}
        <div className="border-t border-stone/20">
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <Link to={`/case-studies/${next.slug}`} data-testid="case-next" className="group">
              <div className="text-xs tracking-[0.14em] uppercase text-stone">{t("Next deployment", "अगली तैनाती")}</div>
              <div className="font-serif text-2xl md:text-3xl mt-2 text-ink group-hover:text-moss transition-colors duration-300 flex items-center gap-3">
                {next.city} <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </Link>
            <Link to="/#contact" className="inline-flex items-center rounded-full bg-forest text-bone text-sm tracking-[0.1em] uppercase px-8 py-4 hover:bg-moss transition-colors duration-300">
              {t("Request a Pilot", "पायलट का अनुरोध करें")}
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
